package es.cragma.fichajefacil.ui.screens.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.clickable
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.KeyboardType
import es.cragma.fichajefacil.data.AppRepository
import es.cragma.fichajefacil.model.User
import es.cragma.fichajefacil.ui.components.CompanyLogoBadge
import es.cragma.fichajefacil.ui.components.StyledButtonPrimary
import es.cragma.fichajefacil.ui.i18n.AppLanguage
import es.cragma.fichajefacil.ui.i18n.LocalizedStrings
import es.cragma.fichajefacil.ui.theme.*

@Composable
fun CompanySettingsScreen(
    currentUser: User,
    repository: AppRepository,
    currentLanguage: AppLanguage = AppLanguage.SPANISH
) {
    val s = remember(currentLanguage) { LocalizedStrings.get(currentLanguage) }
    val companyId = currentUser.companyId ?: ""
    val company = repository.getCompany(companyId)

    var newPositionText by remember { mutableStateOf("") }
    var newDepartmentText by remember { mutableStateOf("") }
    var allowUnverifiedByDefault by remember { mutableStateOf(company?.allowUnverifiedLocationByDefault ?: true) }
    var statusMessage by remember { mutableStateOf<String?>(null) }

    fun refresh() {
        // Triggers recomposition
    }

    if (company == null) {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Text("No hay información de empresa disponible.")
        }
        return
    }

    LazyColumn(
        modifier = Modifier
            .fillMaxSize()
            .background(CragmaBgLight)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        item {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = CragmaNavyDark),
                shape = RoundedCornerShape(16.dp)
            ) {
                Column(modifier = Modifier.padding(18.dp)) {
                    Text("⚙ Configuración de Empresa", fontSize = 12.sp, color = Color(0xFF94A3B8))
                    Text(company.name, fontSize = 20.sp, fontWeight = FontWeight.Bold, color = Color.White)
                    Text("CIF: ${company.cif} • Plan ${company.plan.displayName}", fontSize = 12.sp, color = CragmaBlueSoft)
                }
            }
        }

        if (statusMessage != null) {
            item {
                Surface(
                    color = Color(0xFFDCFCE7),
                    shape = RoundedCornerShape(8.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = statusMessage!!,
                        color = Color(0xFF15803D),
                        fontSize = 12.sp,
                        modifier = Modifier.padding(10.dp)
                    )
                }
            }
        }

        // Section 1: Identidad de Marca y Nombre Comercial
        item {
            var editableCompanyName by remember { mutableStateOf(company.name) }
            var editableCompanyLogo by remember { mutableStateOf(company.logoUrl ?: "🏢") }

            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                shape = RoundedCornerShape(16.dp)
            ) {
                Column(modifier = Modifier.padding(18.dp)) {
                    Text(
                        text = "1. Identidad de Marca y Nombre Comercial",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Text(
                        text = "Personaliza el nombre e isotipo/logo con el que tus empleados verán la aplicación.",
                        fontSize = 12.sp,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(top = 2.dp, bottom = 12.dp)
                    )

                    OutlinedTextField(
                        value = editableCompanyName,
                        onValueChange = { editableCompanyName = it },
                        label = { Text("Nombre Comercial / Marca de la Empresa") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )

                    Spacer(modifier = Modifier.height(12.dp))

                    Text(
                        text = "Logo / Isotipo de la Empresa:",
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Spacer(modifier = Modifier.height(6.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        CompanyLogoBadge(
                            logoUrl = editableCompanyLogo,
                            companyName = editableCompanyName.ifBlank { "EM" },
                            modifier = Modifier.size(48.dp)
                        )

                        val presetLogos = listOf("🏢", "💻", "⚡", "🚀", "🛡️", "🌐", "💼", "🔬", "🏭", "🎨")

                        Row(
                            modifier = Modifier.weight(1f),
                            horizontalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            presetLogos.forEach { emoji ->
                                Surface(
                                    modifier = Modifier.clickable { editableCompanyLogo = emoji },
                                    shape = RoundedCornerShape(8.dp),
                                    color = if (editableCompanyLogo == emoji) CragmaBlueAccent else Color(0xFFF1F5F9)
                                ) {
                                    Text(
                                        text = emoji,
                                        fontSize = 16.sp,
                                        modifier = Modifier.padding(6.dp)
                                    )
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(14.dp))

                    StyledButtonPrimary(
                        text = "Guardar Nombre y Logo",
                        onClick = {
                            if (repository.updateCompanyNameAndLogo(companyId, editableCompanyName, editableCompanyLogo)) {
                                statusMessage = "Identidad de empresa actualizada correctamente."
                                refresh()
                            }
                        }
                    )
                }
            }
        }

        // Section 1: Puestos de Trabajo
        item {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                shape = RoundedCornerShape(16.dp)
            ) {
                Column(modifier = Modifier.padding(18.dp)) {
                    Text(
                        text = "1. Puestos de Trabajo Configurados",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        color = CragmaNavyDark
                    )
                    Text(
                        text = "Estos puestos estarán disponibles en los desplegables al dar de alta o editar trabajadores.",
                        fontSize = 12.sp,
                        color = CragmaTextSecondary,
                        modifier = Modifier.padding(top = 2.dp, bottom = 12.dp)
                    )

                    // Existing positions chips
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        company.positions.forEach { pos ->
                            Surface(
                                color = Color(0xFFF1F5F9),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                Text(
                                    text = pos,
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = CragmaNavyDark,
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp)
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(14.dp))

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        OutlinedTextField(
                            value = newPositionText,
                            onValueChange = { newPositionText = it },
                            label = { Text("Añadir Nuevo Puesto") },
                            placeholder = { Text("Ej: Analista de Datos") },
                            singleLine = true,
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp)
                        )

                        Button(
                            onClick = {
                                if (repository.addPositionToCompany(companyId, newPositionText)) {
                                    statusMessage = "Puesto '$newPositionText' añadido correctamente."
                                    newPositionText = ""
                                    refresh()
                                }
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = CragmaNavyDark)
                        ) {
                            Text("+ Añadir", fontSize = 12.sp)
                        }
                    }
                }
            }
        }

        // Section 2: Departamentos
        item {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                shape = RoundedCornerShape(16.dp)
            ) {
                Column(modifier = Modifier.padding(18.dp)) {
                    Text(
                        text = "2. Departamentos de la Empresa",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        color = CragmaNavyDark
                    )
                    Text(
                        text = "Departamentos disponibles para estructurar la plantilla.",
                        fontSize = 12.sp,
                        color = CragmaTextSecondary,
                        modifier = Modifier.padding(top = 2.dp, bottom = 12.dp)
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        company.departments.forEach { dept ->
                            Surface(
                                color = Color(0xFFEFF6FF),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                Text(
                                    text = dept,
                                    fontSize = 12.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = CragmaBlueAccent,
                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp)
                                )
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(14.dp))

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        OutlinedTextField(
                            value = newDepartmentText,
                            onValueChange = { newDepartmentText = it },
                            label = { Text("Añadir Nuevo Departamento") },
                            placeholder = { Text("Ej: Marketing, Calidad") },
                            singleLine = true,
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp)
                        )

                        Button(
                            onClick = {
                                if (repository.addDepartmentToCompany(companyId, newDepartmentText)) {
                                    statusMessage = "Departamento '$newDepartmentText' añadido correctamente."
                                    newDepartmentText = ""
                                    refresh()
                                }
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = CragmaNavyDark)
                        ) {
                            Text("+ Añadir", fontSize = 12.sp)
                        }
                    }
                }
            }
        }

        // Section 3: Política de Fichaje y Ubicación
        item {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                shape = RoundedCornerShape(16.dp)
            ) {
                Column(modifier = Modifier.padding(18.dp)) {
                    Text(
                        text = "3. Política General de Fichaje",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        color = CragmaNavyDark
                    )

                    Spacer(modifier = Modifier.height(12.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                text = "Permitir fichajes sin ubicación por defecto",
                                fontSize = 14.sp,
                                fontWeight = FontWeight.Bold,
                                color = CragmaNavyDark
                            )
                            Text(
                                text = "Si se desactiva, los nuevos empleados requerirán obligatoriamente ubicación compartida para fichar.",
                                fontSize = 12.sp,
                                color = CragmaTextSecondary
                            )
                        }

                        Switch(
                            checked = allowUnverifiedByDefault,
                            onCheckedChange = {
                                allowUnverifiedByDefault = it
                                repository.updateCompanyLocationPolicy(companyId, it)
                                statusMessage = "Política de ubicación actualizada."
                            }
                        )
                    }
                }
            }
        }

        // Section 4: Ubicaciones y Centros de Trabajo
        item {
            var newWpName by remember { mutableStateOf("") }
            var newWpAddress by remember { mutableStateOf("") }
            var newWpRadius by remember { mutableStateOf("300") }

            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                shape = RoundedCornerShape(16.dp)
            ) {
                Column(modifier = Modifier.padding(18.dp)) {
                    Text(
                        text = "4. Centros de Trabajo y Ubicaciones de Empresa",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Text(
                        text = "Agrega sedes, oficinas o plantas para verificar automáticamente si los fichajes se realizan dentro del área de trabajo autorizada.",
                        fontSize = 12.sp,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(top = 2.dp, bottom = 12.dp)
                    )

                    // Registered Workplaces List
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        company.workplaces.forEach { wp ->
                            Surface(
                                color = MaterialTheme.colorScheme.surfaceVariant,
                                shape = RoundedCornerShape(12.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Row(
                                    modifier = Modifier.padding(12.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column {
                                        Text(wp.name, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                                        Text(wp.address, fontSize = 11.sp, color = MaterialTheme.colorScheme.onSurfaceVariant)
                                        Text("Área de cobertura: ${wp.radiusMeters}m de radio (Lat: ${wp.latitude}, Lon: ${wp.longitude})", fontSize = 10.sp, color = CragmaBlueAccent)
                                    }
                                    Surface(
                                        color = Color(0xFFDCFCE7),
                                        shape = RoundedCornerShape(8.dp)
                                    ) {
                                        Text("🏢 Sede Activa", color = Color(0xFF15803D), fontSize = 10.sp, fontWeight = FontWeight.Bold, modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp))
                                    }
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(14.dp))

                    Text("Añadir Nueva Sede o Centro de Trabajo:", fontSize = 13.sp, fontWeight = FontWeight.Bold)
                    Spacer(modifier = Modifier.height(6.dp))

                    OutlinedTextField(
                        value = newWpName,
                        onValueChange = { newWpName = it },
                        label = { Text("Nombre del Centro de Trabajo") },
                        placeholder = { Text("Ej: Sede Barcelona, Nave Logística...") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    OutlinedTextField(
                        value = newWpAddress,
                        onValueChange = { newWpAddress = it },
                        label = { Text("Dirección del Centro") },
                        placeholder = { Text("Ej: Av. Diagonal 123, Barcelona") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        OutlinedTextField(
                            value = newWpRadius,
                            onValueChange = { newWpRadius = it },
                            label = { Text("Radio Área (Metros)") },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(12.dp)
                        )

                        Button(
                            onClick = {
                                val radiusInt = newWpRadius.toIntOrNull() ?: 300
                                if (repository.addWorkplaceToCompany(companyId, newWpName, newWpAddress, 40.4167, -3.7037, radiusInt)) {
                                    statusMessage = "Centro de trabajo '$newWpName' registrado correctamente."
                                    newWpName = ""
                                    newWpAddress = ""
                                    refresh()
                                }
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary)
                        ) {
                            Text("+ Registrar Sede", fontSize = 12.sp)
                        }
                    }
                }
            }
        }
    }
}
